/**
 * V70G8H — 공지 본문 하이퍼링크 URL 검증 / 클릭 분류
 *
 * 입력 허용: https:// · http:// 절대 주소, '/' 로 시작하는 ERP 내부 경로.
 *   - '//host', '/\host' (프로토콜 상대 주소) 와 공백·제어 문자, 그 외 스킴(javascript:, data:, mailto: 등)은 거부.
 * 클릭 분류: 현재 origin 과 같으면 내부(SPA 이동), http(s) 다른 origin 이면 외부(새 탭), 그 외는 무시.
 *   - 저장된 HTML 은 FE DOMPurify + 서버 sanitize-html + 렌더 DOMPurify 로 이미 정화되며, 여기서 한 번 더 판정한다.
 */

export type LinkInputResult =
  | { ok: true; href: string; kind: 'internal' | 'external' }
  | { ok: false; reason: string };

export const LINK_INPUT_RULE = 'https://, http:// 로 시작하는 주소 또는 / 로 시작하는 ERP 내부 경로만 입력할 수 있습니다.';

// eslint-disable-next-line no-control-regex
const CONTROL_OR_SPACE = /[\u0000-\u001F\u007F\s]/;

export function validateLinkInput(raw: string | null | undefined): LinkInputResult {
  const v = (raw ?? '').trim();
  if (!v) return { ok: false, reason: 'URL을 입력하세요.' };
  if (CONTROL_OR_SPACE.test(v)) return { ok: false, reason: 'URL에 공백이나 제어 문자를 넣을 수 없습니다.' };
  if (v.startsWith('/')) {
    if (v.startsWith('//') || v.includes('\\')) return { ok: false, reason: LINK_INPUT_RULE };
    return { ok: true, href: v, kind: 'internal' };
  }
  if (/^https?:\/\//i.test(v)) {
    let u: URL;
    try {
      u = new URL(v);
    } catch {
      return { ok: false, reason: '올바른 URL 형식이 아닙니다.' };
    }
    if (!u.hostname || u.username || u.password) return { ok: false, reason: '올바른 URL 형식이 아닙니다.' };
    return { ok: true, href: v, kind: 'external' };
  }
  return { ok: false, reason: LINK_INPUT_RULE };
}

export type ResolvedLink =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; href: string }
  | { kind: 'mailto' }
  | { kind: 'invalid' };

/** 렌더된 <a href> 클릭 시 이동 방식 판정 */
export function resolveLinkClick(href: string | null | undefined, origin: string): ResolvedLink {
  const h = (href ?? '').trim();
  if (!h) return { kind: 'invalid' };
  if (/^mailto:/i.test(h)) return { kind: 'mailto' };
  let u: URL;
  try {
    u = new URL(h, origin);
  } catch {
    return { kind: 'invalid' };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return { kind: 'invalid' };
  if (u.origin === origin) return { kind: 'internal', path: `${u.pathname}${u.search}${u.hash}` };
  return { kind: 'external', href: u.href };
}
